import { DragDropContext, Draggable, Droppable, OnDragEndResponder } from '@hello-pangea/dnd';
import { Box } from '../../components/UI-CHAKRA/Box';
import { Text } from '../../components/UI-CHAKRA/Text';
import { ManualMedicine, Medicine, OriginEnum } from '../../interfaces/medicine';
import { PrescriberService, UpdateOrderMedicine } from '../../shared/prescriber';
import { usePrescriptionStore } from '../../stores/prescriptionStore';
import { useModelStore } from '../../stores/modelStore';
import { isEmpty } from 'lodash';
import { AnimatePresence } from 'framer-motion';
import { FadeSlideMotionBox } from '../../components/Motion/FadeSlideMotionBox';
import { FaPlus } from 'react-icons/fa';
import { Flex } from '../../components/UI-CHAKRA/Flex';
import { Divider } from '../../components/UI-CHAKRA/Divider';
import { PrescriptionModels } from '../my-central/Models/PrescriptionModels';
import { Exam } from '../../interfaces/exam';
import { MedicalReport } from '../../interfaces/medicalReport';
import { MedicalReferral } from '../../interfaces/MedicalReferral';
import { MedicalCertificate } from '../../interfaces/medicalCertificate';
import { MedicinesInteractions } from '../../interfaces/medicine-drug-interactions';
import withSuspense from '../../components/withLazyComponent';
import { lazy, useEffect, useMemo, useRef } from 'react';
import { IncompletePrescriptionsAlert } from '../../components/IncompletePrescriptionsAlert';
import { Formula } from '../../interfaces/formula';
import PrescriptionFormulaContent from './PrescriptionFormulaContent/PrescriptionFormulaContent';
import { get } from 'lodash';
import { SncrNoBalanceCard } from './SncrNumbering/components/SncrNoBalanceCard';
import { isMedFlowOperationAllowed } from '../../helpers/medFlowPermissions';
import { isCorrelatoMedicine } from '../../helpers/medicineEditionUtils';

const ExamCard = withSuspense(lazy(() => import('../../components/search/ExamCard')));
const PrescriptionObservationCard = withSuspense(lazy(() => import('./PrescriptionObservationCard')));
const MedicalCertificateCard = withSuspense(lazy(() => import('./MedicalCertificateCard')));
const ManualMedicineCard = withSuspense(lazy(() => import('../../components/search/ManualMedicineCard')));
const MedicineCard = withSuspense(lazy(() => import('../../components/search/MedicineCard')));
const MedicalReferralCard = withSuspense(lazy(() => import('../../components/search/MedicalReferralCard')));
const MedicalReportCard = withSuspense(lazy(() => import('../../components/search/MedicalReportCard')));

interface PrescriptionContentProps {
  prescriptionObservation: string;
  onEditPrescriptionObservation(): void;
  onConfirmRemovePrescriptionObservation(): void;
  addPrescriptionObservation(): void;
  exams: Exam[] | undefined;
  medicalReferrals: MedicalReferral[] | undefined;
  medicalCertificates: MedicalCertificate[] | undefined;
  medicalReports: MedicalReport[] | undefined;
  allItems: {
    allMedicines: (Medicine | ManualMedicine)[];
    nextOrder: number;
  };
  formulas: Formula[] | undefined;
  onRemoveManualMedicine(manualMedicine: ManualMedicine): Promise<void>;
  onEditManualMedicine(props: { medicine: ManualMedicine; index: null }): void;
  onRemoveMedicine(medicine: Medicine): Promise<void>;
  medicineDrugInteractions: MedicinesInteractions[];
  onEditMedicine(props: { medicine: Medicine; index: number }): void;
  onClickInteraction(interactionId: string, medicineId: number): void;
  onDeleteExam(exam: Exam): Promise<void>;
  onUpdateExam(exam: Exam): Promise<void>;
  onEditExam(index: number): void;
  enableTussCodeOnInterface: boolean;
  onEditMedicalCertificate(medicalCertificate: MedicalCertificate): void;
  onDeleteMedicalCertificate(medicalCertificateId: number): Promise<void>;
  onRemoveMedicalReferral(medicalReferralId: number): Promise<void>;
  onEditMedicalReferral(referral: MedicalReferral): void;
  onEditMedicalReport(medicalReport: any): void;
  onRemoveMedicalReport(medicalReportId: number): Promise<void>;
  onIncludeConcomitanceMedicine(medicine: Medicine, searchMedicineId?: number): void;
  setSelectedFormula: (formula: Formula | null) => void;
  selectedFormula: Formula | null;
  isFormulaFormDialogOpen: boolean;
  setIsFormulaFormDialogOpen: (isOpen: boolean) => void;
  isFormulaContent?: boolean;
}

function PrescriptionContent(props: PrescriptionContentProps) {
  const {
    prescriptionObservation,
    onEditPrescriptionObservation,
    onConfirmRemovePrescriptionObservation,
    addPrescriptionObservation,
    exams,
    formulas,
    medicalCertificates,
    medicalReferrals,
    medicalReports,
    allItems,
    onRemoveManualMedicine,
    onEditManualMedicine,
    onRemoveMedicine,
    medicineDrugInteractions,
    onEditMedicine,
    onClickInteraction,
    onDeleteExam,
    onUpdateExam,
    onEditExam,
    enableTussCodeOnInterface,
    onEditMedicalCertificate,
    onDeleteMedicalCertificate,
    onRemoveMedicalReferral,
    onEditMedicalReferral,
    onEditMedicalReport,
    onRemoveMedicalReport,
    onIncludeConcomitanceMedicine,
    setSelectedFormula,
    selectedFormula,
    isFormulaFormDialogOpen,
    setIsFormulaFormDialogOpen,
    isFormulaContent,
  } = props;

  const prescriber = usePrescriptionStore((store) => store.prescriber());
  const { prescriberConfiguration, prescription } = usePrescriptionStore();
  const prescriptionItemsAmount = usePrescriptionStore((store) => store.prescriptionItemsAmount());
  const onUpdatePrescriptionObservation = usePrescriptionStore((store) => store.onUpdatePrescriptionObservation);
  const medFlow = prescriberConfiguration?.Features?.medFlowIntegration;
  const isMedFlow = (origem?: OriginEnum) => origem === OriginEnum.MEDFLOW;

  const lastMedicineRef = useRef<HTMLDivElement | null>(null);
  const prevItemsAmountRef = useRef(prescriptionItemsAmount);
  const isUndoingModelApplication = useModelStore((store) => store.isUndoingApplication);

  /**
   * Limpa a observação quando o último item sai da prescrição.
   *
   * Fica de fora enquanto o desfazer de um modelo está rodando: ele remove os itens um a um e,
   * quando o último sai, é ele quem restaura a observação anterior do médico. Sem essa guarda as
   * duas escritas concorrem e o `null` pode resolver por último, apagando a observação restaurada.
   * O `prevItemsAmountRef` continua sendo atualizado antes do early return, então o desfazer não
   * deixa o ref dessincronizado.
   */
  useEffect(() => {
    const prevAmount = prevItemsAmountRef.current;
    prevItemsAmountRef.current = prescriptionItemsAmount;

    if (isUndoingModelApplication) return;

    if (prevAmount > 0 && prescriptionItemsAmount === 0 && prescriptionObservation.trim()) {
      onUpdatePrescriptionObservation(null);
    }
  }, [prescriptionItemsAmount, prescriptionObservation, onUpdatePrescriptionObservation, isUndoingModelApplication]);
  const previousCountRef = useRef(allItems.allMedicines.length);
  const enableAutoScroll = get(prescriberConfiguration, 'Features.enableAutoScroll', false);
  const isSncrNumberingEnabled = get(prescriberConfiguration, 'Features.enableSNCR', false);
  const showSncrNoBalanceCard =
    isSncrNumberingEnabled && prescription?.ValidacaoConformidadeSNCR === false && allItems.allMedicines.length > 0;

  useEffect(() => {
    const currentCount = allItems.allMedicines.length;
    const wasAdded = currentCount > previousCountRef.current;
    previousCountRef.current = currentCount;

    if (!enableAutoScroll || !wasAdded) return;

    if (lastMedicineRef.current) {
      lastMedicineRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [allItems.allMedicines, enableAutoScroll]);

  const incompleteMedicinesCount = useMemo(() => {
    return allItems.allMedicines.filter((medicine) => {
      return !medicine.Posologia && !isCorrelatoMedicine(medicine);
    }).length;
  }, [allItems.allMedicines]);

  const reorder = (list: any[], startIndex: number, endIndex: number): (Medicine | ManualMedicine)[] => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const onDragEnd: OnDragEndResponder = ({ destination, source }) => {
    if (!destination) {
      return;
    }
    if (destination.index === source.index) {
      return;
    }
    let reorderedMedicines = reorder(allItems.allMedicines, source.index, destination.index);

    reorderedMedicines = reorderedMedicines.map((medicine, i) => ({ ...medicine, Ordem: i + 1 }));

    allItems.allMedicines = reorderedMedicines;

    const updatePayload: UpdateOrderMedicine = {
      MedicamentosOrdenados: reorderedMedicines.map((medicine) => {
        const isManual = !!('Manual' in medicine);
        if (isManual) {
          return {
            idPrescricaoMedicamento: medicine.idPrescricaoMedicamentoManual || 0,
            Ordem: medicine.Ordem || 0,
            Manual: isManual,
          };
        }

        const regularMedicine = medicine as Medicine;
        return {
          idPrescricaoMedicamento: regularMedicine.idPrescricaoMedicamento || 0,
          Ordem: medicine.Ordem || 0,
          Manual: isManual,
        };
      }),
    };

    PrescriberService.updateOrderMedicinePrescription(updatePayload, prescription?.idPrescricao || 0).catch((error) => {
      // Não fazer nada pois isso não pode interromper o fluxo de mudança de ordem.
    });
  };
  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <AnimatePresence>
        {incompleteMedicinesCount > 0 && <IncompletePrescriptionsAlert count={incompleteMedicinesCount} />}

        <Droppable droppableId="medicines">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {isFormulaContent && allItems.allMedicines.length > 0 && (
                <Box mt={2}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Text fontSize={'16px'} fontWeight={500} color={'typography.main'} whiteSpace={'nowrap'}>
                      Receita médica
                    </Text>
                    <Divider m={'0 8px'} />
                  </div>
                </Box>
              )}
              {showSncrNoBalanceCard && (
                <Box my={2}>
                  <SncrNoBalanceCard />
                </Box>
              )}
              {allItems.allMedicines.map((medicine, index) => {
                if ('Manual' in medicine && medicine.Manual) {
                  return (
                    <Draggable
                      draggableId={`${medicine.idPrescricaoMedicamentoManual}`}
                      index={index}
                      key={medicine.idPrescricaoMedicamentoManual}
                    >
                      {(draggableProvided) => (
                        <div
                          ref={(el) => {
                            draggableProvided.innerRef(el);
                            if (index === allItems.allMedicines.length - 1) {
                              lastMedicineRef.current = el;
                            }
                          }}
                          {...draggableProvided.draggableProps}
                          {...draggableProvided.dragHandleProps}
                        >
                          <ManualMedicineCard
                            medicine={medicine}
                            onRemoveMedicine={onRemoveManualMedicine}
                            onEditMedicine={onEditManualMedicine}
                            index={index + 1}
                            key={medicine.idPrescricaoMedicamentoManual}
                            disabled={isMedFlow(medicine.Origem) && !isMedFlowOperationAllowed(medFlow, 'medicaments', 'delete')}
                            disableEdit={isMedFlow(medicine.Origem) && !isMedFlowOperationAllowed(medFlow, 'medicaments', 'update')}
                          />
                        </div>
                      )}
                    </Draggable>
                  );
                }

                if ('IdMedicamento' in medicine) {
                  return (
                    <Draggable
                      draggableId={`${medicine.idPrescricaoMedicamento}`}
                      index={index}
                      key={medicine.idPrescricaoMedicamento}
                    >
                      {(draggableProvided) => (
                        <div
                          ref={(el) => {
                            draggableProvided.innerRef(el);
                            if (index === allItems.allMedicines.length - 1) {
                              lastMedicineRef.current = el;
                            }
                          }}
                          {...draggableProvided.draggableProps}
                          {...draggableProvided.dragHandleProps}
                        >
                          <MedicineCard
                            medicine={medicine}
                            onRemoveMedicine={onRemoveMedicine}
                            medicineDrugInteractions={medicineDrugInteractions.find(
                              (medicineDrug) => medicineDrug.medicineId === medicine.IdMedicamento
                            )}
                            onEditMedicine={onEditMedicine}
                            onClickInteraction={onClickInteraction}
                            index={index + 1}
                            key={medicine.idPrescricaoMedicamento}
                            disabled={isMedFlow(medicine.Origem) && !isMedFlowOperationAllowed(medFlow, 'medicaments', 'delete')}
                            disableEdit={isMedFlow(medicine.Origem) && !isMedFlowOperationAllowed(medFlow, 'medicaments', 'update')}
                            includeConcomitanceMedicine={onIncludeConcomitanceMedicine}
                          />
                        </div>
                      )}
                    </Draggable>
                  );
                }

                return null;
              })}
            </div>
          )}
        </Droppable>
      </AnimatePresence>

      {!isEmpty(formulas) && !isFormulaContent && (
        <PrescriptionFormulaContent
          prescriptionId={prescription?.idPrescricao || 0}
          doctorId={prescriber?.idPrescritor || 0}
          isFormulaFormDialogOpen={isFormulaFormDialogOpen}
          setIsFormulaFormDialogOpen={setIsFormulaFormDialogOpen}
          selectedFormula={selectedFormula}
          setSelectedFormula={setSelectedFormula}
        />
      )}

      {!!prescriberConfiguration?.HabilitarExame && !isEmpty(exams) && (
        <Box mt={2}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Text fontSize={'16px'} fontWeight={500} color={'typography.main'}>
              Exames
            </Text>
            <Divider m={'0 8px'} />
          </div>
        </Box>
      )}

      <Flex flexDir={'column'} gap={'10px'}>
        {(exams || []).map((exam, index) => {
          return (
            <ExamCard
              key={index}
              exam={exam}
              index={index + 1}
              onDelete={onDeleteExam}
              onUpdateExam={onUpdateExam}
              onEdit={() => onEditExam(index)}
              enableTuss={enableTussCodeOnInterface}
              shouldBeOpen={false}
              shouldCollapse={false}
              disabled={isMedFlow(exam.Origem) && !isMedFlowOperationAllowed(medFlow, 'exams', 'delete')}
              disableEdit={isMedFlow(exam.Origem) && !isMedFlowOperationAllowed(medFlow, 'exams', 'update')}
            />
          );
        })}
      </Flex>

      {!isEmpty(medicalCertificates) && (
        <Box mt={2}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Text fontSize={'16px'} fontWeight={500} color={'typography.main'}>
              Atestado
            </Text>
            <Divider m={'0 8px'} />
          </div>
        </Box>
      )}

      <AnimatePresence>
        {(medicalCertificates || []).map((medicalCertificate) => {
          return (
            <MedicalCertificateCard
              key={medicalCertificate.idPrescricaoAtestado}
              medicalCertificate={medicalCertificate}
              onEdit={onEditMedicalCertificate}
              onDelete={onDeleteMedicalCertificate}
              professionalIssuer={prescriber?.Conselho || ''}
              disabled={isMedFlow(medicalCertificate.Origem) && !isMedFlowOperationAllowed(medFlow, 'medicalStatement', 'delete')}
              disableEdit={isMedFlow(medicalCertificate.Origem) && !isMedFlowOperationAllowed(medFlow, 'medicalStatement', 'update')}
            />
          );
        })}
      </AnimatePresence>

      {!isEmpty(medicalReferrals) && (
        <Box mt={2}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Text fontSize={'16px'} fontWeight={500} color={'typography.main'}>
              Encaminhamentos
            </Text>
            <Divider m={'0 8px'} />
          </div>
        </Box>
      )}

      <AnimatePresence>
        {(medicalReferrals || []).map((medicalReferral) => {
          return (
            <MedicalReferralCard
              key={medicalReferral.idEncaminhamentoPrescricao}
              referral={medicalReferral}
              onDelete={onRemoveMedicalReferral}
              onEdit={onEditMedicalReferral}
              disabled={isMedFlow(medicalReferral.Origem) && !isMedFlowOperationAllowed(medFlow, 'referral', 'delete')}
              disableEdit={isMedFlow(medicalReferral.Origem) && !isMedFlowOperationAllowed(medFlow, 'referral', 'update')}
            />
          );
        })}
      </AnimatePresence>

      {!isEmpty(medicalReports) && (
        <Box mt={2}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Text fontSize={'16px'} fontWeight={500} color={'typography.main'}>
              Relatório(s)
            </Text>
            <Divider m={'0 8px'} />
          </div>
        </Box>
      )}

      <AnimatePresence>
        {(medicalReports || []).map((medicalReport, index) => {
          return (
            <MedicalReportCard
              index={index + 1}
              key={medicalReport.idPrescricaoLaudoMedico}
              onEdit={onEditMedicalReport}
              onDelete={onRemoveMedicalReport}
              medicalReport={medicalReport}
              disabled={isMedFlow(medicalReport.Origem) && !isMedFlowOperationAllowed(medFlow, 'report', 'delete')}
              disableEdit={isMedFlow(medicalReport.Origem) && !isMedFlowOperationAllowed(medFlow, 'report', 'update')}
            />
          );
        })}
      </AnimatePresence>

      <AnimatePresence>
        {prescriberConfiguration?.ExibirObservacoes && allItems.allMedicines.length > 0 && !prescriptionObservation && (
          <FadeSlideMotionBox>
            <Flex
              minHeight={'40px'}
              alignItems={'center'}
              backgroundColor={'gray.lightest'}
              borderRadius={'20px'}
              border={'1px solid #E9ECEE'}
              padding={'0 10px'}
              onClick={addPrescriptionObservation}
              cursor={'pointer'}
              mt={2}
            >
              <FaPlus style={{ marginRight: 10, width: '10px' }} color={'#868E96'} />
              <Text color={'#868E96'} fontSize={'14px'} fontWeight={'600'}>
                Adicionar observação
              </Text>
            </Flex>
          </FadeSlideMotionBox>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {prescriptionObservation && (
          <PrescriptionObservationCard
            observation={prescriptionObservation}
            onEdit={onEditPrescriptionObservation}
            onDelete={onConfirmRemovePrescriptionObservation}
          />
        )}
      </AnimatePresence>

      <PrescriptionModels />
    </DragDropContext>
  );
}

export default PrescriptionContent;
